// src/pages/UserPage.js
import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MdAccountCircle, MdInsertChart, MdFileDownload, MdDownload } from 'react-icons/md'; 
import { FaPlay, FaCalendarAlt, FaChartLine, FaCrown, FaGem, FaSeedling } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  background-color: #E3E3E3;
  min-height: calc(100vh - 120px);
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 80px 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ContentContainer = styled.div`
  width: 75%;
  max-width: 800px;
  margin: 0 auto;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 40px;
  font-family: 'Rubik', sans-serif;

  @media (max-width: 768px) {
    width: 100%;
    padding: 25px;
  }
`;

const TabsContainer = styled.div`
  position: relative;
  display: flex;
  margin-bottom: 35px;
  background-color: rgba(46, 46, 46, 0.05);
  border-radius: 8px;
  padding: 4px;

  button {
    flex: 1;
    padding: 12px 0;
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    font-family: 'Rubik', sans-serif;
    transition: all 0.3s;
    border-radius: 6px;
    z-index: 1;
    color: rgba(46, 46, 46, 0.7);

    &.active {
      font-weight: 500;
      color: #2e2e2e;
      background-color: white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:hover:not(.active) {
      color: #555;
    }

    svg {
      margin-right: 8px;
      vertical-align: middle;
    }
  }
`;

const SectionContainer = styled.div`
  background-color: rgba(46, 46, 46, 0.02);
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(46, 46, 46, 0.04);
  }

  h3 {
    font-size: 18px;
    color: #2e2e2e;
    margin-bottom: 20px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 14px;
    color: rgba(46, 46, 46, 0.6);
    margin-bottom: 8px;
  }

  p {
    font-size: 16px;
    color: #2e2e2e;
    padding: 14px 16px;
    background-color: white;
    border: 2px solid rgba(46, 46, 46, 0.1);
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .badge {
    margin-left: auto;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    background-color: rgba(46, 46, 46, 0.05);
    color: #2e2e2e;
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;

  input {
    width: 100%;
    padding: 14px 16px;
    border: 2px solid rgba(46, 46, 46, 0.1);
    border-radius: 10px;
    font-size: 16px;
    font-family: 'Rubik', sans-serif;
    transition: all 0.3s;
    background: rgba(46, 46, 46, 0.02);

    &:focus {
      border-color: #2e2e2e;
      outline: none;
      background: white;
    }

    &:focus + label,
    &:not(:placeholder-shown) + label {
      top: -8px;
      left: 10px;
      font-size: 12px;
      color: #2e2e2e;
      background-color: white;
      padding: 0 4px;
    }
  }

  label {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    background-color: transparent;
    color: rgba(46, 46, 46, 0.6);
    font-size: 16px;
    pointer-events: none;
    transition: all 0.2s ease-out;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background-color: #2e2e2e;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  font-family: 'Rubik', sans-serif;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #444;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: rgba(46, 46, 46, 0.4);
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 18px;
  }
`;

const LogoutButton = styled(Button)`
  margin-top: 30px;
  background-color: transparent;
  color: #d32f2f;
  border: 2px solid #d32f2f;

  &:hover {
    background-color: #d32f2f;
    color: white;
  }

  &:disabled {
    border-color: rgba(211, 47, 47, 0.4);
    color: rgba(211, 47, 47, 0.4);
    background-color: transparent;
  }
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 14px;
  margin: 5px 0;
  padding: 8px 12px;
  background-color: rgba(211, 47, 47, 0.1);
  border-radius: 6px;
  font-family: 'Rubik', sans-serif;
`;

const SuccessMessage = styled.p`
  color: #388e3c;
  font-size: 14px;
  margin: 5px 0;
  padding: 8px 12px;
  background-color: rgba(56, 142, 60, 0.1);
  border-radius: 6px;
  font-family: 'Rubik', sans-serif;
`;

const DownloadsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const DownloadItem = styled.div`
  background-color: white;
  border: 2px solid rgba(46, 46, 46, 0.1);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s;

  &:hover {
    border-color: #2e2e2e;
    transform: translateY(-1px);
  }

  .title {
    flex: 1;
    font-weight: 500;
  }

  .date {
    color: rgba(46, 46, 46, 0.6);
    font-size: 14px;
  }

  .download-btn {
    padding: 8px;
    background: none;
    border: none;
    cursor: pointer;
    color: #2e2e2e;
    transition: all 0.3s;

    &:hover {
      color: #444;
      transform: translateY(-1px);
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;

  button {
    padding: 8px 16px;
    border: 2px solid rgba(46, 46, 46, 0.1);
    border-radius: 8px;
    background: white;
    cursor: pointer;
    transition: all 0.3s;

    &:hover:not(:disabled) {
      border-color: #2e2e2e;
      background-color: #2e2e2e;
      color: white;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;

const UserPage = () => {
  const [activeSection, setActiveSection] = useState('account');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloads, setDownloads] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    total_plays: 0,
    total_downloads: 0,
    recent_downloads: 0,
    weekly_download_limit: 0,
    unlimited_downloads: false,
    favoriteGenre: 'N/A'
  });

  const { user, logout, getUserStats } = useContext(AuthContext);
  const navigate = useNavigate();

  const fetchUserStats = React.useCallback(async () => {
    try {
      if (getUserStats) {
        const userStats = await getUserStats();
        if (userStats) {
          setStats(prevStats => ({
            ...prevStats,
            ...userStats
          }));
        }
      } else {
        setStats(prevStats => ({
          ...prevStats,
          total_plays: 156,
          total_downloads: 42,
          recent_downloads: 12,
          weekly_download_limit: 10,
          unlimited_downloads: false,
          favoriteGenre: 'Electronic'
        }));
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  }, [getUserStats]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserStats();
      await fetchUserDownloads(1);
    };
    initializeData();
  }, [fetchUserStats]);

  const fetchUserDownloads = async (page = 1) => {
    // Implement downloads fetching logic here
    setDownloads([
      {
        id: 1,
        title: 'Summer Vibes',
        downloadDate: '2024-03-15',
        genre: 'Electronic'
      },
      // Add more mock downloads as needed
    ]);
    setTotalPages(3);
    setCurrentPage(page);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    try {
      // Implement password reset logic here
      setSuccess('Password updated successfully');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to update password');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAccountTypeIcon = () => {
    switch (user?.account_type?.toLowerCase()) {
      case 'premier':
        return <FaCrown style={{ color: '#FFD700' }} />;
      case 'pro':
        return <FaGem />;
      default:
        return <FaSeedling />;
    }
  };

  const getAccountTypeLabel = () => {
    return user?.account_type || 'Free Account';
  };

  const formatMemberSince = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'N/A';
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>My Account | Tempo Stock</title>
        <meta name="description" content="Manage your Tempo Stock account settings and view your download history" />
      </Helmet>

      <ContentContainer>
        <TabsContainer>
          <button
            className={activeSection === 'account' ? 'active' : ''}
            onClick={() => setActiveSection('account')}
          >
            <MdAccountCircle /> Account
          </button>
          <button
            className={activeSection === 'stats' ? 'active' : ''}
            onClick={() => setActiveSection('stats')}
          >
            <MdInsertChart /> Stats
          </button>
          <button
            className={activeSection === 'downloads' ? 'active' : ''}
            onClick={() => setActiveSection('downloads')}
          >
            <MdFileDownload /> Downloads
          </button>
        </TabsContainer>

        {activeSection === 'account' && (
          <>
            <SectionContainer>
              <h3><MdAccountCircle /> Account Information</h3>
              <DetailSection>
                <label>Email Address</label>
                <p>{user?.email}</p>
              </DetailSection>
              <DetailSection>
                <label>Account Type</label>
                <p>
                  {getAccountTypeIcon()}
                  {getAccountTypeLabel()}
                </p>
              </DetailSection>
              <DetailSection>
                <label>Member Since</label>
                <p>
                  <FaCalendarAlt />
                  {formatMemberSince(user?.created_at)}
                </p>
              </DetailSection>
              <DetailSection>
                <label>Weekly Download Limit</label>
                <p>
                  <FaChartLine />
                  {stats.unlimited_downloads ? 'Unlimited Downloads' : `${stats.weekly_download_limit || 0} tracks per week`}
                </p>
              </DetailSection>
            </SectionContainer>

            <SectionContainer>
              <h3>Change Password</h3>
              <form onSubmit={handlePasswordReset}>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
                
                <InputContainer>
                  <input
                    type="password"
                    id="oldPassword"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="oldPassword">Current Password</label>
                </InputContainer>

                <InputContainer>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="newPassword">New Password</label>
                </InputContainer>

                <InputContainer>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                </InputContainer>

                <Button type="submit">Update Password</Button>
              </form>
            </SectionContainer>

            <LogoutButton onClick={handleLogout}>
              Logout
            </LogoutButton>
          </>
        )}

        {activeSection === 'stats' && (
          <SectionContainer>
            <h3><FaChartLine /> Your Statistics</h3>
            <DetailSection>
              <label>Total Plays</label>
              <p>
                <FaPlay />
                {stats.total_plays} tracks played
              </p>
            </DetailSection>
            <DetailSection>
              <label>Total Downloads</label>
              <p>
                <MdDownload />
                {stats.total_downloads} tracks downloaded
              </p>
            </DetailSection>
            <DetailSection>
              <label>Recent Downloads</label>
              <p>
                <FaCalendarAlt />
                {stats.recent_downloads} tracks this week
              </p>
            </DetailSection>
            <DetailSection>
              <label>Favorite Genre</label>
              <p>
                <FaPlay />
                {stats.favoriteGenre || 'N/A'}
              </p>
            </DetailSection>
          </SectionContainer>
        )}

        {activeSection === 'downloads' && (
          <SectionContainer>
            <h3><MdFileDownload /> Download History</h3>
            <DownloadsList>
              {downloads.map(download => (
                <DownloadItem key={download.id}>
                  <span className="title">{download.title}</span>
                  <span className="date">{formatDate(download.downloadDate)}</span>
                  <button className="download-btn" title="Download again">
                    <MdDownload />
                  </button>
                </DownloadItem>
              ))}
            </DownloadsList>
            
            <Pagination>
              <button
                onClick={() => fetchUserDownloads(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                onClick={() => fetchUserDownloads(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </Pagination>
          </SectionContainer>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default UserPage;